import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import FirebaseTest from '../components/FirebaseTest';
import './CompanyHistory.css';
import { db } from '../firebase';
import { collection, getDocs, addDoc } from 'firebase/firestore';

const Certification = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getCertificateImageSrc = (cert) => {
    const url = (cert?.imageUrl || '').trim();
    if (url) return url;

    const raw = (cert?.image || '').trim();
    if (!raw) return '';
    if (raw.startsWith('/')) return raw;
    if (!raw.includes('.')) return `/${raw}.png`;
    return `/${raw}`;
  };

  const getFallbackImageSrc = (cert) => {
    const name = (cert?.name || '').toLowerCase();
    if (cert?.category === '특허권' || name.includes('patent')) return '/certificate_patent.png';
    return '/logo.png';
  };

  const getCertificatePdfHref = (cert) => {
    const pdfUrl = (cert?.pdfUrl || '').trim();
    if (pdfUrl) return pdfUrl;
    const pdfFileName = (cert?.pdf || '').trim();
    if (!pdfFileName) return '';
    return `/certificates/${encodeURIComponent(pdfFileName)}`;
  };

  const sidebarItems = [
    { name: '인증서', path: '/ko/certification-detail' }
  ];

  const normalizeCategory = (category) => {
    const c = (category || '').trim();
    const map = {
      'ISO Certificates (Korean)': 'ISO 인증서 (한글)',
      'ISO Certificates (English)': 'ISO 인증서 (영문)',
      'ASME Certificates': 'ASME 인증서',
      'KC Certificates': 'KC 인증서',
      'Patents': '특허권',
    };
    return map[c] || c;
  };

  // Firebase에서 인증서 데이터 불러오기
  const fetchCertificates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Firebase 연결 시도 중...');
      const certificatesCollection = collection(db, 'certificates');
      console.log('컬렉션 참조 생성됨:', certificatesCollection);
      
      const certificatesSnapshot = await getDocs(certificatesCollection);
      console.log('문서 스냅샷 획득:', certificatesSnapshot.docs.length, '개 문서');
      
      const certificatesData = certificatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const normalizedCertificatesData = certificatesData.map((cert) => ({
        ...cert,
        category: normalizeCategory(cert.category),
      }));
      
      console.log('문서 데이터:', normalizedCertificatesData);

      const defaultCertificates = [
        {
          category: "ISO 인증서 (한글)",
          certificates: [
            { name: "ISO 9001:2015", image: "certificate_isokor1.png", pdf: "ISO9001_국문.pdf", order: 1 },
            { name: "ISO 14001:2015", image: "certificate_isokor2.png", pdf: "ISO14001_국문.pdf", order: 2 },
            { name: "ISO 45001:2018", image: "certificate_isokor3.png", pdf: "ISO45001_국문.pdf", order: 3 }
          ]
        },
        {
          category: "ISO 인증서 (영문)",
          certificates: [
            { name: "ISO 9001:2015", image: "certificate_isoeng1.png", pdf: "ISO9001_영문.pdf", order: 4 },
            { name: "ISO 14001:2015", image: "certificate_isoeng2.png", pdf: "ISO14001_영문.pdf", order: 5 },
            { name: "ISO 45001:2018", image: "certificate_isoeng3.png", pdf: "ISO45001_영문.pdf", order: 6 }
          ]
        },
        {
          category: "ASME 인증서",
          certificates: [
            { name: "ASME U Stamp", image: "certificate_asme.png", pdf: "ASME 인증서 (U Designator)~25.10.14.pdf", order: 7 }
          ]
        },
        {
          category: "KC 인증서",
          certificates: [
            { name: "KC 인증서", image: "certificate_kc1.png", pdf: "KC인증서(~27.02.25).pdf", order: 8 }
          ]
        },
        {
          category: "특허권",
          certificates: [
            { name: "판형열교환기전열판의가스켓 특허", image: "certificate_patent.png", pdf: "판형열교환기전열판의가스켓-특허2015.pdf", order: 9 }
          ]
        }
      ];

      if (normalizedCertificatesData.length === 0) {
        setCertificates(defaultCertificates);
        return;
      }

      // 카테고리별로 그룹화
      const groupedCertificates = normalizedCertificatesData.reduce((acc, cert) => {
        if (!acc[cert.category]) {
          acc[cert.category] = [];
        }
        acc[cert.category].push(cert);
        return acc;
      }, {});

      // 배열 형태로 변환하고 카테고리 순서대로 정렬
      const categoryOrder = [
        "ISO 인증서 (한글)",
        "ISO 인증서 (영문)",
        "ASME 인증서",
        "KC 인증서",
        "특허권",
      ];

      const certificatesArray = categoryOrder
        .filter((category) => groupedCertificates[category])
        .map((category) => ({
          category,
          certificates: groupedCertificates[category].sort(
            (a, b) => (a.order || 0) - (b.order || 0)
          ),
        }));

      setCertificates(certificatesArray.length ? certificatesArray : defaultCertificates);
      
    } catch (err) {
      console.error('인증서 데이터를 불러오는 중 오류 발생:', err);
      console.error('에러 상세:', err.message);
      setError(`인증서 데이터를 불러오는 중 오류가 발생했습니다: ${err.message}`);
      
      // 에러 발생 시에도 기본 데이터 표시
      const defaultCertificates = [
        {
          category: "ISO 인증서 (한글)",
          certificates: [
            { name: "ISO 9001:2015", image: "certificate_isokor1.png", pdf: "ISO9001_국문.pdf", order: 1 },
            { name: "ISO 14001:2015", image: "certificate_isokor2.png", pdf: "ISO14001_국문.pdf", order: 2 },
            { name: "ISO 45001:2018", image: "certificate_isokor3.png", pdf: "ISO45001_국문.pdf", order: 3 }
          ]
        },
        {
          category: "ISO 인증서 (영문)",
          certificates: [
            { name: "ISO 9001:2015", image: "certificate_isoeng1.png", pdf: "ISO9001_영문.pdf", order: 4 },
            { name: "ISO 14001:2015", image: "certificate_isoeng2.png", pdf: "ISO14001_영문.pdf", order: 5 },
            { name: "ISO 45001:2018", image: "certificate_isoeng3.png", pdf: "ISO45001_영문.pdf", order: 6 }
          ]
        },
        {
          category: "ASME 인증서",
          certificates: [
            { name: "ASME U Stamp", image: "certificate_asme.png", pdf: "ASME 인증서 (U Designator)~25.10.14.pdf", order: 7 }
          ]
        },
        {
          category: "KC 인증서",
          certificates: [
            { name: "KC 인증서", image: "certificate_kc1.png", pdf: "KC인증서(~27.02.25).pdf", order: 8 }
          ]
        },
        {
          category: "특허권",
          certificates: [
            { name: "판형열교환기전열판의가스켓 특허", image: "certificate_patent.png", pdf: "판형열교환기전열판의가스켓-특허2015.pdf", order: 9 }
          ]
        }
      ];
      setCertificates(defaultCertificates);
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 불러오기
  useEffect(() => {
    fetchCertificates();
  }, []);

  // NOTE: 다운로드는 window.open 대신 <a href target="_blank">로 처리 (팝업 차단/클릭 미동작 방지)

  // 초기 인증서 데이터 추가
  const addInitialData = async () => {
    try {
      const certificatesCollection = collection(db, 'certificates');
      
      const initialCertificates = [
        // ISO 인증서 (한글) - 1~3번
        { category: "ISO 인증서 (한글)", name: "ISO 9001:2015", image: "certificate_isokor1.png", pdf: "ISO9001_국문.pdf", order: 1 },
        { category: "ISO 인증서 (한글)", name: "ISO 14001:2015", image: "certificate_isokor2.png", pdf: "ISO14001_국문.pdf", order: 2 },
        { category: "ISO 인증서 (한글)", name: "ISO 45001:2018", image: "certificate_isokor3.png", pdf: "ISO45001_국문.pdf", order: 3 },
        
        // ISO 인증서 (영문) - 4~6번
        { category: "ISO 인증서 (영문)", name: "ISO 9001:2015", image: "certificate_isoeng1.png", pdf: "ISO9001_영문.pdf", order: 4 },
        { category: "ISO 인증서 (영문)", name: "ISO 14001:2015", image: "certificate_isoeng2.png", pdf: "ISO14001_영문.pdf", order: 5 },
        { category: "ISO 인증서 (영문)", name: "ISO 45001:2018", image: "certificate_isoeng3.png", pdf: "ISO45001_영문.pdf", order: 6 },
        
        // ASME 인증서 - 7번
        { category: "ASME 인증서", name: "ASME U Stamp", image: "certificate_asme.png", pdf: "ASME 인증서 (U Designator)~25.10.14.pdf", order: 7 },
        
        // KC 인증서 - 8번
        { category: "KC 인증서", name: "KC 인증서", image: "certificate_kc1.png", pdf: "KC인증서(~27.02.25).pdf", order: 8 },
        
        // 특허권 - 9번
        { category: "특허권", name: "판형열교환기전열판의가스켓 특허", image: "certificate_patent.png", pdf: "판형열교환기전열판의가스켓-특허2015.pdf", order: 9 }
      ];

      console.log('초기 인증서 데이터 추가 시작...');
      
      for (const cert of initialCertificates) {
        await addDoc(certificatesCollection, cert);
        console.log(`인증서 추가됨: ${cert.name}`);
      }
      
      console.log('모든 인증서 데이터가 성공적으로 추가되었습니다!');
      alert('초기 인증서 데이터가 성공적으로 추가되었습니다!');
      
      // 데이터 추가 후 다시 불러오기
      fetchCertificates();
      
    } catch (error) {
      console.error('초기 데이터 추가 중 오류 발생:', error);
      alert(`초기 데이터 추가 중 오류가 발생했습니다: ${error.message}`);
    }
  };

  // 로딩 상태 표시
  if (loading) {
    return (
      <>
        <Sidebar category="인증현황" items={sidebarItems} />
        <div className="company-history-page">
          <div className="history-container">
            <h1 className="page-category-title">인증서</h1>
            <div className="section-container">
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p>인증서 데이터를 불러오는 중...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // 에러 상태 표시
  if (error) {
    return (
      <>
        <Sidebar category="인증현황" items={sidebarItems} />
        <div className="company-history-page">
          <div className="history-container">
            <h1 className="page-category-title">인증서</h1>
            <div className="section-container">
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ color: 'red', marginBottom: '1rem' }}>
                  <p><strong>오류 발생:</strong> {error}</p>
                </div>
                <div style={{ color: '#666', marginBottom: '2rem' }}>
                  <p>Firebase 연결에 문제가 있을 수 있습니다. 아래 테스트 버튼으로 연결을 확인해보세요.</p>
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <FirebaseTest />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <button onClick={addInitialData} style={{ marginRight: '1rem', padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    초기 인증서 데이터 추가
                  </button>
                </div>
                <button onClick={fetchCertificates} style={{ marginTop: '1rem', padding: '0.5rem 1rem', backgroundColor: '#0066cc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                  다시 시도
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Sidebar category="인증현황" items={sidebarItems} />
      <div className="company-history-page">
        <div className="history-container">
          <h1 className="page-category-title">인증서</h1>
          
          <div className="section-container">
            <div className="certificates-section">
              {certificates.map((group, groupIndex) => (
                <div key={groupIndex} className="certificate-group">
                  <h3 className="certificate-category-title">{group.category}</h3>
                  <div className="certificate-grid">
                    {group.certificates.map((cert, index) => (
                      <div key={index} className="certificate-item">
                        <a
                          className="certificate-image-container"
                          href={getCertificatePdfHref(cert) || '#'}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => {
                            const href = getCertificatePdfHref(cert);
                            if (!href) {
                              e.preventDefault();
                              alert('❌ 연결된 PDF가 없습니다. 관리자에서 PDF를 업로드해주세요.');
                            }
                          }}
                        >
                          <img 
                            src={getCertificateImageSrc(cert)}
                            alt={cert.name}
                            className="certificate-image"
                            onError={(e) => {
                              // prevent infinite error loop
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = getFallbackImageSrc(cert);
                            }}
                          />
                          <div className="certificate-overlay">
                            <span className="download-text">클릭하여 PDF 다운로드</span>
                          </div>
                        </a>
                        <div className="certificate-info">
                          <h4 className="certificate-name">{cert.name}</h4>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Certification;